import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { ServiceStateBase } from '@shared/services/service-state-base.types';
import dayjs from 'dayjs';
import { NewsletterPost } from '../models/newsletter.types';
import { NewsletterHttpService } from './newsletter.http.service';

const BANNER_DISMISSED_KEY = 'twinny:newsletter:bannerDismissed';
const READ_IDS_KEY = 'twinny:newsletter:readIds';
const FRESHNESS_MONTHS = 3;

@Injectable({ providedIn: 'root' })
export class NewsletterService extends ServiceStateBase {
    private readonly newsletterS = inject(NewsletterHttpService);
    private readonly translate = inject(TranslateService);
    private readonly destroyRef = inject(DestroyRef);

    readonly posts = signal<NewsletterPost[]>([]);
    readonly postToShow = signal<NewsletterPost | null>(null);
    readonly dismissedIds = signal<ReadonlySet<number>>(this.readDismissed());

    /** IDs the user has already seen — drives the topbar bell's unread badge. */
    readonly readIds = signal<ReadonlySet<number>>(this.readSet(READ_IDS_KEY));

    /** Count of releases not yet opened from the bell. */
    readonly unreadCount = computed(() => {
        const read = this.readIds();
        return this.posts().filter(p => !read.has(p.id)).length;
    });

    /**
     * Most recent release published within the last 3 months that the user
     * has not dismissed. Drives the dashboard "What's new" banner.
     */
    readonly latestFreshPost = computed<NewsletterPost | null>(() => {
        const dismissed = this.dismissedIds();
        const cutoff = dayjs().subtract(FRESHNESS_MONTHS, 'month');
        const fresh = this.posts()
            .filter(p => !dismissed.has(p.id) && dayjs(p.releasedAt).isAfter(cutoff))
            .sort((a, b) => dayjs(b.releasedAt).valueOf() - dayjs(a.releasedAt).valueOf());
        return fresh[0] ?? null;
    });

    constructor() {
        super();
        this.reqPosts();
        // Re-fetch when the user switches language so the localized fields refresh.
        this.translate.onLangChange
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.reqPosts());
    }

    private reqPosts() {
        this.newsletterS.get().subscribe({
            next: ({ data: posts }) => this.posts.set(posts),
            error: error => this.onError('Error fetching posts', error),
        });
    }

    openPost(post: NewsletterPost) {
        this.markRead(post.id);
        this.newsletterS.getById(post.id).subscribe({
            next: post => this.postToShow.set(post),
            error: error => this.onError('Error fetching post', error),
        });
        this.postToShow.set(post);
    }

    closePost() {
        this.postToShow.set(null);
    }

    dismissBanner(id: number) {
        const next = new Set(this.dismissedIds());
        next.add(id);
        this.dismissedIds.set(next);
        this.writeSet(BANNER_DISMISSED_KEY, next);
    }

    markRead(id: number) {
        if (this.readIds().has(id)) return;
        const next = new Set(this.readIds());
        next.add(id);
        this.readIds.set(next);
        this.writeSet(READ_IDS_KEY, next);
    }

    markAllRead() {
        const next = new Set(this.posts().map(p => p.id));
        this.readIds.set(next);
        this.writeSet(READ_IDS_KEY, next);
    }

    private readDismissed(): ReadonlySet<number> {
        return this.readSet(BANNER_DISMISSED_KEY);
    }

    private readSet(key: string): ReadonlySet<number> {
        try {
            const raw = localStorage.getItem(key);
            if (!raw) return new Set();
            const parsed = JSON.parse(raw);
            if (!Array.isArray(parsed)) return new Set();
            return new Set(parsed.filter((n): n is number => typeof n === 'number'));
        } catch {
            return new Set();
        }
    }

    private writeSet(key: string, ids: ReadonlySet<number>) {
        try {
            localStorage.setItem(key, JSON.stringify(Array.from(ids)));
        } catch {
            // localStorage may be unavailable (Safari private mode etc.) — silently no-op
        }
    }
}
